import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotate,
  faCheck,
  faCode,
  faEarthAmericas,
  faKey,
  faLink,
  faLock,
  faPlug,
  faRobot,
  faShieldHalved,
  faSpinner,
  faTrashCan,
  faTriangleExclamation,
  faUserShield,
} from "@fortawesome/free-solid-svg-icons";
import { useCallback, useEffect, useState } from "react";
import { Button, ConfirmModal, ErrorState, PageHeader } from "../components/UI";
import { useToast } from "../context/ToastContext";
import api, { getData } from "../services/api";
import { getPublicAppUrl } from "../utils/publicUrl";

const PUTER_SCRIPT_ID = "knovera-puter-sdk";
const PUTER_SCRIPT_URL = "https://js.puter.com/v2/";
const PUTER_LOAD_TIMEOUT_MS = 15000;
let puterLoadPromise = null;

function preparePuterSdk(forceReload = false) {
  if (window.puter?.auth?.signIn) return Promise.resolve(window.puter);
  if (puterLoadPromise && !forceReload) return puterLoadPromise;

  const existing = document.getElementById(PUTER_SCRIPT_ID);
  if (existing) existing.remove();
  puterLoadPromise = null;

  puterLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    let settled = false;

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      script.removeEventListener("load", finish);
      script.removeEventListener("error", fail);
    };
    const finish = () => {
      if (settled) return;
      if (!window.puter?.auth?.signIn) {
        fail();
        return;
      }
      settled = true;
      cleanup();
      resolve(window.puter);
    };
    const fail = () => {
      if (settled) return;
      settled = true;
      cleanup();
      script.remove();
      puterLoadPromise = null;
      reject(
        new Error(
          navigator.onLine
            ? "Puter sign-in could not be loaded. Check whether it was blocked and try again."
            : "You appear to be offline. Reconnect and try again.",
        ),
      );
    };
    const timeoutId = window.setTimeout(fail, PUTER_LOAD_TIMEOUT_MS);

    script.addEventListener("load", finish);
    script.addEventListener("error", fail);
    script.id = PUTER_SCRIPT_ID;
    script.src = PUTER_SCRIPT_URL;
    script.async = true;
    document.head.appendChild(script);
  });

  return puterLoadPromise;
}

function integrationFrom(response) {
  return getData(response)?.integration || { connected: false };
}

function readableDate(value) {
  if (!value) return "Not yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return new Intl.DateTimeFormat("en", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function humanizePuterError(error) {
  if (!navigator.onLine)
    return "You appear to be offline. Reconnect and try again.";
  const text = `${error?.message || error?.error || ""}`.toLowerCase();
  if (
    text.includes("cancel") ||
    text.includes("closed") ||
    text.includes("denied")
  ) {
    return "Puter sign-in was cancelled. Your current connection was not changed.";
  }
  if (text.includes("popup") || text.includes("blocked")) {
    return "The sign-in window was blocked. Allow popups for this page and try again.";
  }
  return error?.message || "Puter could not be connected. Please try again.";
}

function humanizeApiError(error, fallback) {
  if (!navigator.onLine)
    return "You appear to be offline. Reconnect and try again.";
  return error?.message || fallback;
}

function PuterConnectionPanel() {
  const toast = useToast();
  const [integration, setIntegration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [sdkStatus, setSdkStatus] = useState(
    window.puter?.auth?.signIn ? "ready" : "loading",
  );
  const [sdkError, setSdkError] = useState("");

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await api.get("/integrations/puter");
      setIntegration(integrationFrom(response));
    } catch (error) {
      setLoadError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const prepareSdk = useCallback((forceReload = false) => {
    setSdkStatus("loading");
    setSdkError("");
    return preparePuterSdk(forceReload)
      .then(() => {
        setSdkStatus("ready");
      })
      .catch((error) => {
        setSdkStatus("error");
        setSdkError(humanizePuterError(error));
      });
  }, []);

  useEffect(() => {
    let active = true;
    preparePuterSdk()
      .then(() => {
        if (active) setSdkStatus("ready");
      })
      .catch((error) => {
        if (!active) return;
        setSdkStatus("error");
        setSdkError(humanizePuterError(error));
      });
    return () => {
      active = false;
    };
  }, []);

  const connect = () => {
    if (sdkStatus !== "ready" || !window.puter?.auth?.signIn) {
      toast.show(
        "Puter sign-in is still being prepared. Wait for it to be ready and try again.",
        "info",
      );
      return;
    }

    setBusy(true);
    let signInRequest;
    try {
      // Keep this call synchronous with the button event so the browser preserves
      // the user activation required to open Puter's authentication window.
      signInRequest = window.puter.auth.signIn({ request_auth: true });
    } catch (error) {
      toast.show(humanizePuterError(error), "error");
      setBusy(false);
      return;
    }

    Promise.resolve(signInRequest)
      .then((result) => {
        if (!result?.token) {
          throw new Error(
            "Puter did not provide authorization. Please try signing in again.",
          );
        }
        return api.post("/integrations/puter/connect", { token: result.token });
      })
      .then((response) => {
        setIntegration(integrationFrom(response));
        toast.show(
          integration?.connected
            ? "Puter account authorization was refreshed."
            : "Puter account connected. Public assistants can now answer without visitor sign-in.",
        );
      })
      .catch((error) => {
        toast.show(humanizePuterError(error), "error");
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      const response = await api.delete("/integrations/puter");
      setIntegration(integrationFrom(response));
      setConfirmDisconnect(false);
      toast.show(
        "Puter was disconnected. Public AI responses are now paused.",
        "info",
      );
    } catch (error) {
      toast.show(
        humanizeApiError(
          error,
          "Puter could not be disconnected. Please try again.",
        ),
        "error",
      );
    } finally {
      setBusy(false);
    }
  };

  const connected = Boolean(integration?.connected);
  const needsAttention =
    connected &&
    (integration?.status === "error" || Boolean(integration?.lastError));

  return (
    <article
      className={`panel puter-connection ${connected && !needsAttention ? "is-connected" : "needs-connection"}`}
      aria-labelledby="puter-connection-title"
    >
      <span className="connection-signal-rail" aria-hidden="true" />
      <header className="puter-connection-header">
        <div className="puter-heading">
          <span className="puter-mark" aria-hidden="true">
            <FontAwesomeIcon icon={faShieldHalved} />
          </span>
          <div>
            <span className="eyebrow">Central AI authorization</span>
            <h2 id="puter-connection-title">Puter AI connection</h2>
            <p>
              One trusted administrator account funds server-side answers for
              every website assistant. Visitors never see a Puter login.
            </p>
          </div>
        </div>
        {!loading && !loadError && (
          <span
            className={`connection-status ${connected && !needsAttention ? "is-healthy" : "is-disconnected"}`}
          >
            <i aria-hidden="true" />
            {connected
              ? needsAttention
                ? "Connected · needs attention"
                : "Connected"
              : "Not connected"}
          </span>
        )}
      </header>

      {loading ? (
        <div
          className="puter-loading"
          aria-label="Loading Puter connection status"
          aria-busy="true"
        >
          <div className="skeleton-line skeleton-line-wide" />
          <div className="skeleton-line" />
          <div className="skeleton-line skeleton-line-short" />
        </div>
      ) : loadError ? (
        <ErrorState message={loadError} onRetry={loadStatus} />
      ) : connected ? (
        <>
          <div className="credential-vault">
            <div className="vault-identity">
              <span className="vault-icon" aria-hidden="true">
                <FontAwesomeIcon icon={faUserShield} />
              </span>
              <div>
                <small>Authorized Puter account</small>
                <strong>
                  {integration.username || "Connected administrator"}
                </strong>
              </div>
            </div>
            <span className="vault-link" aria-hidden="true">
              <i />
              <FontAwesomeIcon icon={faLink} />
              <i />
            </span>
            <div className="vault-destination">
              <span aria-hidden="true">
                <FontAwesomeIcon icon={faRobot} />
              </span>
              <div>
                <small>Available to</small>
                <strong>All website assistants</strong>
              </div>
            </div>
            <div className="vault-seal">
              <FontAwesomeIcon icon={faLock} aria-hidden="true" />
              <span>Credential encrypted server-side</span>
            </div>
          </div>

          <dl className="connection-metadata">
            <div>
              <dt>Connected</dt>
              <dd>{readableDate(integration.connectedAt)}</dd>
            </div>
            <div>
              <dt>Connected by</dt>
              <dd>
                {integration.connectedBy?.name ||
                  integration.connectedBy?.email ||
                  integration.connectedBy ||
                  "Administrator"}
              </dd>
            </div>
            <div>
              <dt>Last validated</dt>
              <dd>{readableDate(integration.lastValidatedAt)}</dd>
            </div>
          </dl>

          {integration.lastError && (
            <div className="connection-warning" role="status">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <div>
                <strong>Authorization needs attention</strong>
                <p>{integration.lastError}</p>
              </div>
            </div>
          )}

          <footer className="puter-connection-actions">
            <Button
              variant="secondary"
              icon={faArrowsRotate}
              onClick={connect}
              disabled={busy}
            >
              {busy ? "Connecting…" : "Reconnect account"}
            </Button>
            <Button
              variant="danger"
              icon={faTrashCan}
              onClick={() => setConfirmDisconnect(true)}
              disabled={busy}
            >
              Disconnect
            </Button>
          </footer>
        </>
      ) : (
        <div className="puter-disconnected">
          <div className="disconnected-copy">
            <FontAwesomeIcon icon={faKey} aria-hidden="true" />
            <div>
              <strong>Connect once for the whole platform</strong>
              <p>
                Sign in as the administrator who will authorize AI usage.
                FlowChat sends public chat requests through the server using
                this connection, so visitors can ask questions immediately.
              </p>
            </div>
          </div>
          <Button
            icon={busy ? faSpinner : faPlug}
            onClick={connect}
            disabled={busy}
          >
            {busy ? "Opening Puter…" : "Connect Puter account"}
          </Button>
        </div>
      )}

      <div className="connection-privacy-note">
        <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
        <span>
          The authorization token is sent directly to the server and is never
          saved in this browser.
        </span>
      </div>

      <ConfirmModal
        open={confirmDisconnect}
        title="Disconnect Puter account?"
        message="This removes the encrypted server authorization. Public chat AI will stop answering on every connected website until an administrator reconnects an account."
        confirmLabel="Disconnect account"
        danger
        busy={busy}
        onConfirm={disconnect}
        onClose={() => !busy && setConfirmDisconnect(false)}
      />
    </article>
  );
}

export default function SettingsPage() {
  const publicUrl = getPublicAppUrl();
  return (
    <>
      <PageHeader
        eyebrow="Workspace reference"
        title="Settings & deployment"
        description="Operational details for a secure FlowChat installation."
      />
      <section className="settings-grid">
        <PuterConnectionPanel />
        <article className="panel settings-lead">
          <span className="settings-icon">
            <FontAwesomeIcon icon={faEarthAmericas} />
          </span>
          <span className="eyebrow">Public application URL</span>
          <h2>{publicUrl}</h2>
          <p>
            Widget scripts and public chatbot configuration are served from this
            address. Set <code>REACT_APP_PUBLIC_APP_URL</code> before creating a
            production build.
          </p>
        </article>
        <article className="panel settings-item">
          <FontAwesomeIcon icon={faRobot} />
          <div>
            <h3>Centralized model access</h3>
            <p>
              Puter requests run server-side through the connected administrator
              account. Visitors never authenticate with Puter, and configured
              model failures return a recoverable chat error.
            </p>
          </div>
        </article>
        <article className="panel settings-item">
          <FontAwesomeIcon icon={faShieldHalved} />
          <div>
            <h3>Domain boundaries</h3>
            <p>
              Register every exact hostname that may embed a widget, including
              www and staging variants. Public retrieval rejects unapproved
              origins.
            </p>
          </div>
        </article>
        <article className="panel settings-item">
          <FontAwesomeIcon icon={faKey} />
          <div>
            <h3>Protected credentials</h3>
            <p>
              The Puter credential is encrypted at rest, protected by admin
              authentication, never returned by APIs, and never included in the
              public widget JavaScript.
            </p>
          </div>
        </article>
        <article className="panel settings-item">
          <FontAwesomeIcon icon={faCode} />
          <div>
            <h3>Production delivery</h3>
            <p>
              Build the React client, serve it behind HTTPS, use a strong JWT
              secret, and configure precise client/public URLs in the Express
              environment.
            </p>
          </div>
        </article>
      </section>
    </>
  );
}
